import FileUpload from "components/FileUpload";
import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import MainLayout from "layouts/MainLayout";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { addBailRight } from "services/bailRightService";
import { fetchSuspect } from "services/lealIntityService";
import {
  BailRight,
  BailRightType,
  CaseCreation,
  CrimeTypeForInvestigationType,
  Roles,
  Suspect,
  Witness,
} from "types";
import { viewIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

const defaultValue: BailRight = {
  suspect: null,
  prosecutor_document_upload: null,
  receipt_of_payment_upload: null,
  status: "forwarded_to_coordinator",
  crime_registration_number: "",
  incident_registration_number: "",
  prosecutor_registration_number: "",
  suspect_fullname: "",
  suspect_gender: "",
  suspect_age: null,
  suspect_nationality: "",
  suspect_identification_number: "",
  crime_for_suspected: "",
  current_imprisonment_location: "",
  detention_start_date: null,
  court_bail_application_file: null,
  court_registration_number: "",
  bail_amount: 0,
  bail_evidence_description: "",
  court_name: "",
  court_open_day: null,
  court_hearing_date: null,
  coordinator_assigned_prosecutor: "",
  userid_coordinator_assigned_prosecutor: "",
  prosecutor_summary: "",
};

const BailRightRequest: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const caseData: CaseCreation | null = state?.caseData || null;
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [tab] = useState(t("suspect_information"));
  // Wizard step (0..3): Case & Crime → Court → Suspect → Evidence.
  const [step, setStep] = useState(0);

  const [suspects, setSuspects] = useState<Suspect[]>([]);
  const [filteredSusect, setFilteredSusect] = useState<Suspect[]>([]);
  const [selectedDefendant] = useState<Suspect>();

  const [formState, setFormState] = useState<BailRight>(defaultValue);

  const [selectedEntity, setSelectedEntity] = useState<Suspect | Witness>();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState({
    suspect: false,
    witness: false,
  });

  useEffect(() => {
    fetchSuspect()
      .then((data) => {
        setSuspects(data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (loginData) {
      setFormState((prevState) => ({
        ...prevState,
        receiving_prosecutor: hasRole(loginData?.roles, [Roles.prosecutor])
          ? loginData?.user_id
          : null,
        receiving_police: hasRole(loginData?.roles, [Roles.member])
          ? loginData?.user_id
          : null,
      }));
    }
  }, [loginData]);

  const handleFile = (name: string, file: File | null) => {
    setFormState({ ...formState, [name]: file });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]:
        name === "suspect_age" ? (value === "" ? null : Number(value)) : value,
    }));
  };

  const validateForm = () => {
    if (!formState?.crime_type) return t("crime_type_required");
    else if (!formState?.suspect && !formState?.suspect_fullname)
      return t("suspect_required");
    // else if (!formState.crime_registration_number)
    //   return t("crime_registration_number_required");
    // else if (!formState.incident_registration_number)
    //   return t("incident_registration_number_required");
    else if (!formState.court_registration_number)
      return t("court_registration_number_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    const formData: BailRight = {
      ...formState,
      admin_unit_id: loginData?.admin_unit || "",
    };
    addBailRight(formData)
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        navigate("/bail-right");
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      });
  };

  const buildView = (data: Suspect | Witness, type: "suspect" | "witness") => {
    return (
      <div>
        <div className="flex flex-row mt-4 border border-[#D4D4D4] p-4 rounded-lg">
          <div className="flex flex-1 items-center gap-2">
            <img
              src={
                data?.picture_file || require("../../images/imageVector.png")
              }
              alt="User"
              className="w-10 h-10 rounded mr-2 cursor-pointer"
              onClick={() => {
                setIsImageModalOpen(true);
                setSelectedEntity(data);
              }}
            />
            <div>
              <p className="text-gray-800 font-semibold">{`${data?.first_name} ${data?.middle_name} ${data?.last_name}`}</p>
              <div className="flex flex-row gap-1 items-center">
                <p className="text-gray-500 text-sm">{data?.age}</p>
                <p>•</p>
                <p className="text-gray-500 text-sm">{data?.phone_number}</p>
                <p>•</p>
                <p className="text-gray-500 text-sm">{data?.nationality}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center mt-4 gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsDetailModalOpen({
                  suspect: type === "suspect",
                  witness: type === "witness",
                });
                setSelectedEntity(data);
              }}
            >
              {viewIcon}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const STEPS = [
    t("case_crime_information", { defaultValue: "Case & Crime" }),
    t("court_information", { defaultValue: "Court" }),
    t("suspect_information"),
    t("evidence_and_documents", { defaultValue: "Evidence & Documents" }),
  ];
  const isLastStep = step === STEPS.length - 1;

  return (
    <MainLayout>
      <div className="incidentRegistration max-w-screen-lg">
        {/* Page header */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm mb-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-700 via-red-600 to-red-500" />
          <div className="flex items-start gap-4 p-5">
            <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-2xl shadow-md shadow-red-200">
              ⚖️
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900">
                {t("register_bail_right", {
                  defaultValue: "Register Bail Right",
                })}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t("bail_right_request_desc", {
                  defaultValue:
                    "Capture the suspect and bail details to file a bail-right request for review.",
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
          <form onSubmit={handleSubmit}>
            <div className="bg-white shadow-md rounded-lg p-6 mt-4 ">
              <div>
                {/* Stepper */}
                <div className="flex items-center gap-2 mb-6">
                  {STEPS.map((label, i) => (
                    <React.Fragment key={i}>
                      <button
                        type="button"
                        onClick={() => setStep(i)}
                        className="flex items-center gap-2 group"
                      >
                        <span
                          className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                            i < step
                              ? "bg-emerald-600 text-white"
                              : i === step
                                ? "bg-red-600 text-white"
                                : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {i < step ? "✓" : i + 1}
                        </span>
                        <span
                          className={`hidden sm:inline text-xs font-semibold ${
                            i === step ? "text-gray-900" : "text-gray-400"
                          }`}
                        >
                          {label}
                        </span>
                      </button>
                      {i < STEPS.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 rounded ${
                            i < step ? "bg-emerald-500" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div
                  className={
                    tab === t("suspect_information") ? "block" : "hidden"
                  }
                >
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {step === 0 && (
                      <>
                    <div className="col-span-2 flex items-center gap-3 mb-1">
                      <div className="w-1.5 h-5 rounded-full bg-red-600" />
                      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                        {t("case_crime_information", {
                          defaultValue: "Case & Crime Information",
                        })}
                      </h3>
                      <div className="flex-1 border-t border-gray-100" />
                    </div>
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("bail_type")}
                      </label>
                      <select
                        className="border border-gray-300 rounded-lg bg-white p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                        name="bail_type"
                        value={formState?.bail_type}
                        onChange={handleChange}
                      >
                        <option value={""}>{t("select_bail_type")}</option>
                        {Object.entries(BailRightType).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col mt-4 w-full">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("crime_type")}
                      </label>
                      <select
                        className="border border-gray-300 rounded-lg bg-white p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                        name="crime_type"
                        value={formState?.crime_type ?? ""}
                        onChange={handleChange}
                        required
                      >
                        <option value={""}>{t("select_crime_type")}</option>
                        {Object.entries(CrimeTypeForInvestigationType).map(
                          ([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                    {/* <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("bail_amount")}
                      </label>
                      <input
                        type="number"
                        placeholder={t("bail_amount_placeholder")}
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="bail_amount"
                        value={formState?.bail_amount}
                        onChange={handleChange}
                      />
                    </div> */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("crime_registration_number")}
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="crime_registration_number"
                        value={formState?.crime_registration_number ?? ""}
                        onChange={(e) => {
                          setFormState({
                            ...formState,
                            crime_registration_number: e.target.value,
                          });
                        }}
                      />
                    </div>
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("incident_registration_number")}
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="incident_registration_number"
                        value={formState?.incident_registration_number ?? ""}
                        onChange={(e) => {
                          setFormState({
                            ...formState,
                            incident_registration_number: e.target.value,
                          });

                          setFilteredSusect(
                            suspects?.filter(
                              (s) =>
                                s?.Incident_Registration_number ===
                                e.target.value,
                            ) || [],
                          );
                        }}
                      />
                    </div>
                    {formState?.bail_type === "on_court_hearing" && (
                      <div className="flex flex-col mt-4">
                        <label className="text-sm text-gray-700 font-medium mb-2">
                          {t("prosecutor_registration_number")}
                        </label>
                        <input
                          type="text"
                          placeholder="#123456"
                          className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                          name="prosecutor_registration_number"
                          value={
                            formState?.prosecutor_registration_number ?? ""
                          }
                          onChange={handleChange}
                        />
                      </div>
                    )}
                      </>
                    )}
                    {step === 1 && (
                      <>
                    <div className="col-span-2 flex items-center gap-3 mb-1">
                      <div className="w-1.5 h-5 rounded-full bg-red-600" />
                      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                        {t("court_information", {
                          defaultValue: "Court Information",
                        })}
                      </h3>
                      <div className="flex-1 border-t border-gray-100" />
                    </div>
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("court_registration_number")}
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="court_registration_number"
                        value={formState?.court_registration_number ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("court_name")}
                      </label>
                      <input
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="court_name"
                        value={formState?.court_name ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="flex flex-col mt-4 w-full">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("court_open_day")}
                      </label>
                      <input
                        type="date"
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="court_open_day"
                        value={
                          formState?.court_open_day
                            ? new Date(formState.court_open_day)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                        onChange={handleChange}
                      />
                    </div>
                    <div className="flex flex-col mt-4 w-full">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("court_hearing_day")}
                      </label>
                      <input
                        type="date"
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="court_hearing_date"
                        value={
                          formState?.court_hearing_date
                            ? new Date(formState.court_hearing_date)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                        onChange={handleChange}
                      />
                    </div>

                      </>
                    )}
                    {step === 2 && (
                      <>
                    <div className="col-span-2 flex items-center gap-3 mb-1">
                      <div className="w-1.5 h-5 rounded-full bg-red-600" />
                      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                        {t("suspect_information")}
                      </h3>
                      <div className="flex-1 border-t border-gray-100" />
                    </div>
                    {formState?.bail_type === "on_court_hearing" && (
                      <div className="flex flex-col mt-4">
                        <label className="text-sm text-gray-700 font-medium mb-2">
                          {t("select_suspect")}
                        </label>
                        <select
                          className="border border-gray-300 rounded-lg bg-white p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                          value={formState?.suspect ?? ""}
                          name="suspect"
                          onChange={handleChange}
                        >
                          <option value={""}>{t("select_suspect")}</option>
                          {filteredSusect.map((suspect, index) => (
                            <option key={index} value={suspect?.uu_id}>
                              {suspect?.first_name} {suspect?.middle_name}{" "}
                              {suspect?.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formState?.bail_type === "on_investigation" && (
                      <>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            {t("suspect_full_name")}
                          </label>
                          <input
                            placeholder={t("suspect_name_placeholder")}
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="suspect_fullname"
                            value={formState?.suspect_fullname ?? ""}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            {t("gender")}
                          </label>
                          <select
                            className="border border-gray-300 rounded-lg bg-white p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                            name="suspect_gender"
                            value={formState?.suspect_gender ?? ""}
                            onChange={handleChange}
                          >
                            <option value="">{t("select_gender")}</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            {t("age")}
                          </label>
                          <input
                            type="number"
                            placeholder="18"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="suspect_age"
                            value={formState?.suspect_age ?? ""}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            {t("nationality")}
                          </label>
                          <input
                            placeholder="Nationality"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="suspect_nationality"
                            value={formState?.suspect_nationality ?? ""}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            Identification Number
                          </label>
                          <input
                            placeholder="Identification Number"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="suspect_identification_number"
                            value={
                              formState?.suspect_identification_number ?? ""
                            }
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            Detention Start Date
                          </label>
                          <input
                            type="date"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="detention_start_date"
                            value={
                              formState?.detention_start_date
                                ? new Date(formState.detention_start_date)
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                            }
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4 col-span-2">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            Crime For Suspected
                          </label>
                          <textarea
                            rows={4}
                            placeholder="Describe the suspected crime"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="crime_for_suspected"
                            value={formState?.crime_for_suspected ?? ""}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="flex flex-col mt-4 col-span-2">
                          <label className="text-sm text-gray-700 font-medium mb-2">
                            Current Imprisonment Location
                          </label>
                          <input
                            placeholder="Current imprisonment location"
                            className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                            name="current_imprisonment_location"
                            value={
                              formState?.current_imprisonment_location ?? ""
                            }
                            onChange={handleChange}
                          />
                        </div>
                      </>
                    )}
                      </>
                    )}
                  </div>

                  {step === 3 && (
                    <>
                  {selectedDefendant && buildView(selectedDefendant, "suspect")}
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-1.5 h-5 rounded-full bg-red-600" />
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                      {t("evidence_and_documents", {
                        defaultValue: "Evidence & Documents",
                      })}
                    </h3>
                    <div className="flex-1 border-t border-gray-100" />
                  </div>
                  <div className="flex flex-col gap-4 mt-4">
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-gray-700 font-medium mb-2">
                        {t("bail_evidence_description")}
                      </label>
                      <textarea
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                        name="bail_evidence_description"
                        value={formState?.bail_evidence_description ?? ""}
                        rows={4}
                        onChange={handleChange}
                      />
                    </div>
                    <FileUpload
                      label={t("court_bail_application_file")}
                      fileTypes="*"
                      required={true}
                      onChange={(file) =>
                        handleFile("court_bail_application_file", file)
                      }
                    />
                  </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-row mt-6 gap-3 justify-between">
              <div>
                {step > 0 && (
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-[Poppins] font-[500] text-base rounded-md hover:bg-gray-50"
                  >
                    ← {t("back", { defaultValue: "Back" })}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                {!caseData && (
                  <input
                    type="reset"
                    className="border border-[#212121] px-8 py-3 text-[#747474] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
                    value={t("cancel")}
                  />
                )}
                {!isLastStep ? (
                  <button
                    type="button"
                    onClick={() =>
                      setStep((s) => Math.min(STEPS.length - 1, s + 1))
                    }
                    className="bg-red-600 px-8 py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-red-700"
                  >
                    {t("next", { defaultValue: "Next" })} →
                  </button>
                ) : (
                  <input
                    type="submit"
                    className="bg-red-600 px-8 py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-red-700"
                    value={caseData ? t("save") : t("finish")}
                  />
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            selectedEntity?.picture_file ||
            require("../../images/imageVector.png")
          }
          label={
            selectedEntity?.first_name +
            " " +
            selectedEntity?.last_name +
            " " +
            selectedEntity?.last_name
          }
          onClose={() => setIsImageModalOpen(false)}
        />
      )}
      {isDetailModalOpen?.suspect && (
        <SuspectDetailInfo
          suspect={selectedEntity as Suspect}
          isOpen={isDetailModalOpen?.suspect}
          onClose={() =>
            setIsDetailModalOpen({
              suspect: false,
              witness: false,
            })
          }
        />
      )}
    </MainLayout>
  );
};

export default BailRightRequest;
